import { useEffect, useRef } from "react";

const PUBLISHER_ID = process.env.REACT_APP_ADSENSE_PUBLISHER_ID || "";

/**
 * AdSlot renders a real Google AdSense unit when a `slot` (ad-slot ID) is
 * provided. If no slot ID is available yet, it renders a designed placeholder
 * (Auto ads may still fill the surrounding page area). Once you create Ad
 * Units in AdSense, pass their slot IDs via the `slot` prop.
 */
export default function AdSlot({
  size = "inline",
  slot = "",
  format = "auto",
  responsive = true,
  label = "ADVERTISEMENT",
}) {
  const insRef = useRef(null);
  const pushed = useRef(false);

  useEffect(() => {
    if (!PUBLISHER_ID || !slot || pushed.current) return;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch (e) {
      // AdSense not yet loaded; will retry on next mount. Log so debugging
      // is possible without cluttering the console for real users.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[AdSlot] adsbygoogle.push failed:", e);
      }
    }
  }, [slot]);

  const dims = {
    leaderboard: "min-h-[90px] md:min-h-[120px]",
    sidebar: "min-h-[250px]",
    inline: "min-h-[110px] md:min-h-[150px]",
    square: "min-h-[250px]",
  }[size];

  // Real ad unit
  if (PUBLISHER_ID && slot) {
    return (
      <aside
        data-testid={`ad-slot-${size}`}
        className={`w-full my-8 ${dims}`}
        aria-label="Advertisement"
      >
        <div className="overline mb-1">{label}</div>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client={PUBLISHER_ID}
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive={responsive ? "true" : "false"}
        />
      </aside>
    );
  }

  // Placeholder — Auto ads (from index.html loader script) can still place
  // ads elsewhere on the page. Once slot IDs are provided, this becomes a
  // real reserved slot.
  return (
    <aside
      data-testid={`ad-slot-${size}`}
      className={`w-full border border-dashed border-foreground/25 bg-muted/40 my-8 flex flex-col justify-center items-center ${dims}`}
      aria-label="Advertisement"
    >
      <div className="overline">{label}</div>
      <div className="text-[10px] text-foreground/40 font-mono mt-1">
        Google AdSense · Auto ads active · {size}
      </div>
    </aside>
  );
}
